import { sortBy } from "lodash";
import { createSelector } from "reselect";

import {
  AttachmentSearchFields,
  AttachmentSearchGroupLabels,
} from "constants/attachmentSearch";
import {
  NVDDocumentTypeOptions,
  NVDRelatedDocumentTypeValues,
} from "constants/documentTypes";

import { filterBySaleType } from "lib/attachments";

import { getCurrentSale } from "./currentSale";
import {
  getBusinesses,
  getConsignments,
  getFiles,
  getProperties,
} from "./root";

interface File {
  type: string;
  consignment: string;
  envd?: { serialNumber?: string };
}

interface AdditionalProperty {
  PIC?: string;
}

const selectNvdRelatedFiles = createSelector([getFiles], files =>
  Object.values(files).filter(
    (file: File) =>
      NVDRelatedDocumentTypeValues.includes(file.type) || !file.type,
  ),
);

const selectTypeOptions = createSelector([getCurrentSale], currentSale => {
  const saleType = currentSale?.sale_type;
  return NVDDocumentTypeOptions.filter(filterBySaleType(saleType)).map(
    option => ({
      label: option.label,
      value: {
        field: AttachmentSearchFields.Type,
        value: option.value,
      },
    }),
  );
});

const selectVendorPICOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments, getProperties],
  (nvdRelatedFiles, consignments, properties) => {
    const vendorPICSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        const consignment = consignments[file.consignment];
        if (consignment?.vendor_property_id) {
          const property = properties[consignment.vendor_property_id];
          if (property?.PIC) {
            vendorPICSet.add(property.PIC);
          }
        }
      }
    });
    return sortBy(
      Array.from(vendorPICSet).map(pic => ({
        label: pic,
        value: {
          field: AttachmentSearchFields.VendorPIC,
          value: pic,
        },
      })),
      "label",
    );
  },
);

const selectAdditionalPICOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments],
  (nvdRelatedFiles, consignments) => {
    const additionalPICSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        const consignment = consignments[file.consignment];
        if (consignment?.additional_properties) {
          consignment.additional_properties.forEach(
            (prop: AdditionalProperty) => {
              if (prop?.PIC) {
                additionalPICSet.add(prop.PIC);
              }
            },
          );
        }
      }
    });
    return sortBy(
      Array.from(additionalPICSet).map(pic => ({
        label: pic,
        value: {
          field: AttachmentSearchFields.AdditionalPICs,
          value: pic,
        },
      })),
      "label",
    );
  },
);

const selectConsignmentOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments, getBusinesses],
  (nvdRelatedFiles, consignments, businesses) => {
    const consignmentIdSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        consignmentIdSet.add(file.consignment);
      }
    });
    return sortBy(
      Array.from(consignmentIdSet)
        .map(consignmentId => {
          const consignment = consignments[consignmentId];
          if (!consignment) {
            return null;
          }
          const vendor = businesses[consignment.vendor_id];
          const vendorName = vendor?.name || "Unknown";
          const shortCode = vendor?.shortCode ? ` ${vendor.shortCode}` : "";
          return {
            label: `${vendorName}${shortCode} (${consignment.quantity || 0} Hd)`,
            value: {
              field: AttachmentSearchFields.Consignment,
              value: consignmentId,
            },
          };
        })
        .filter(Boolean),
      "label",
    );
  },
);

const selectSerialNumberOptions = createSelector(
  [selectNvdRelatedFiles],
  nvdRelatedFiles => {
    const serialNumberSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.envd?.serialNumber) {
        serialNumberSet.add(file.envd.serialNumber);
      }
    });
    return sortBy(
      Array.from(serialNumberSet).map(serialNumber => ({
        label: serialNumber,
        value: {
          field: AttachmentSearchFields.SerialNumber,
          value: serialNumber,
        },
      })),
      "label",
    );
  },
);

const selectNvdVersionOptions = createSelector(
  [selectNvdRelatedFiles, getConsignments],
  (nvdRelatedFiles, consignments) => {
    const nvdVersionSet = new Set<string>();
    nvdRelatedFiles.forEach((file: File) => {
      if (file.consignment) {
        const consignment = consignments[file.consignment];
        if (consignment?.declaration?.VERSION) {
          nvdVersionSet.add(consignment.declaration.VERSION);
        }
      }
    });
    return sortBy(
      Array.from(nvdVersionSet).map(version => ({
        label: version,
        value: {
          field: AttachmentSearchFields.NVDVersion,
          value: version,
        },
      })),
      "label",
    );
  },
);

const isCompleteOptions = [
  {
    label: "Complete",
    value: {
      field: AttachmentSearchFields.IsComplete,
      value: true,
    },
  },
  {
    label: "Incomplete",
    value: {
      field: AttachmentSearchFields.IsComplete,
      value: false,
    },
  },
];

export const selectAttachmentSearchOptions = createSelector(
  [
    selectTypeOptions,
    selectVendorPICOptions,
    selectAdditionalPICOptions,
    selectConsignmentOptions,
    selectSerialNumberOptions,
    selectNvdVersionOptions,
  ],
  (
    typeOptions,
    vendorPICOptions,
    additionalPICOptions,
    consignmentOptions,
    serialNumberOptions,
    nvdVersionOptions,
  ) => [
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.Type],
      field: AttachmentSearchFields.Type,
      options: typeOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.VendorPIC],
      field: AttachmentSearchFields.VendorPIC,
      options: vendorPICOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.AdditionalPICs],
      field: AttachmentSearchFields.AdditionalPICs,
      options: additionalPICOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.Consignment],
      field: AttachmentSearchFields.Consignment,
      options: consignmentOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.SerialNumber],
      field: AttachmentSearchFields.SerialNumber,
      options: serialNumberOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.NVDVersion],
      field: AttachmentSearchFields.NVDVersion,
      options: nvdVersionOptions,
    },
    {
      label: AttachmentSearchGroupLabels[AttachmentSearchFields.IsComplete],
      field: AttachmentSearchFields.IsComplete,
      options: isCompleteOptions,
    },
  ],
);
